/**
 * Oztrail menu Clarity adapter.
 *
 * Reads Liquid-rendered data-clarity-menu-* attributes and forwards generic
 * menu events to assets/clarity-events.js.
 */

(function () {
  "use strict";

  const TRACKING_SELECTOR = "[data-clarity-menu-action]";
  const HOVER_SELECTOR = "[data-clarity-menu-hover-action]";
  const ROOT_DRAWER_SELECTOR = "#menu-drawer .menu-drawer__navigation-container";
  const SCROLL_CONTAINER_SELECTOR = ".menu-drawer__navigation-container, .menu-drawer__inner-submenu, .mega-menu__list, .mega-menu__content";
  const BELOW_FOLD_TOLERANCE = 8;
  const OPEN_MEASURE_DELAY = 150;
  const HOVER_OPEN_DELAY = 600;

  let currentMenuContext = {};
  let openedPathsThisCycle = {};
  let scrollTrackedThisCycle = false;
  let hoverOpenTimer = null;

  function getClarityApi() {
    return window.aoClarity || null;
  }

  function boolString(value) {
    return value ? "true" : "false";
  }

  function getDatasetValue(element, key) {
    return element && element.dataset ? element.dataset[key] : undefined;
  }

  function getTrackingDetail(element) {
    return {
      surface: getDatasetValue(element, "clarityMenuSurface"),
      path: getDatasetValue(element, "clarityMenuPath"),
      label: getDatasetValue(element, "clarityMenuLabel"),
      destination: getDatasetValue(element, "clarityMenuDestination"),
      linkType: getDatasetValue(element, "clarityMenuLinkType"),
      openSource: getDatasetValue(element, "clarityMenuOpenSource"),
      closeSource: getDatasetValue(element, "clarityMenuCloseSource")
    };
  }

  function hasBelowFoldItems(container) {
    if (!container) return false;

    return container.scrollHeight > container.clientHeight + BELOW_FOLD_TOLERANCE;
  }

  function getRootDrawerContainer() {
    return document.querySelector(ROOT_DRAWER_SELECTOR);
  }

  function getScrollContainerForElement(element) {
    if (!element) return null;

    if (element.matches(".header__icon--menu")) {
      return getRootDrawerContainer();
    }

    const closestScrollContainer = element.closest(SCROLL_CONTAINER_SELECTOR);
    if (closestScrollContainer && hasBelowFoldItems(closestScrollContainer)) {
      return closestScrollContainer;
    }

    const nextElement = element.nextElementSibling;
    if (nextElement) {
      const nestedScrollContainer = nextElement.querySelector(SCROLL_CONTAINER_SELECTOR);
      if (nestedScrollContainer) return nestedScrollContainer;

      if (nextElement.matches(SCROLL_CONTAINER_SELECTOR)) return nextElement;
    }

    return closestScrollContainer || getRootDrawerContainer();
  }

  function setCurrentMenuContext(detail, container) {
    currentMenuContext = {
      surface: detail.surface || currentMenuContext.surface,
      path: detail.path || currentMenuContext.path,
      label: detail.label || currentMenuContext.label,
      hasBelowFoldItems: hasBelowFoldItems(container)
    };
  }

  function pushMenuEvent(action, detail) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "menu_click",
      menu_action: action,
      menu_surface: detail.surface || "",
      menu_path: detail.path || "",
      menu_label: detail.label || "",
      menu_destination: detail.destination || "",
      menu_link_type: detail.linkType || ""
    });
  }

  function trackMenuEvent(action, detail) {
    pushMenuEvent(action, detail);

    const clarityApi = getClarityApi();
    if (!clarityApi || typeof clarityApi.trackMenuEvent !== "function") return;

    clarityApi.trackMenuEvent(action, detail);
  }

  function resetLocalOpenCycle() {
    currentMenuContext = {};
    openedPathsThisCycle = {};
    scrollTrackedThisCycle = false;
  }

  function trackMenuOpen(element, detail) {
    window.setTimeout(function () {
      const container = getScrollContainerForElement(element);
      const detailWithVisibility = Object.assign({}, detail, {
        hasBelowFoldItems: boolString(hasBelowFoldItems(container)),
        scrolled: "false"
      });

      if (!detail.path || detail.path === "root") {
        openedPathsThisCycle = {};
        scrollTrackedThisCycle = false;
      }

      setCurrentMenuContext(detailWithVisibility, container);
      openedPathsThisCycle[detailWithVisibility.path || "root"] = true;
      trackMenuEvent("open", detailWithVisibility);
    }, OPEN_MEASURE_DELAY);
  }

  function trackMenuScrolled(event) {
    if (scrollTrackedThisCycle) return;
    if (!event.currentTarget || event.currentTarget.scrollTop <= BELOW_FOLD_TOLERANCE) return;

    scrollTrackedThisCycle = true;

    trackMenuEvent("scrolled", {
      surface: currentMenuContext.surface,
      path: currentMenuContext.path,
      label: currentMenuContext.label,
      hasBelowFoldItems: boolString(currentMenuContext.hasBelowFoldItems),
      scrolled: "true"
    });
  }

  function isClosingOpenDetails(element) {
    const details = element.closest("details");
    return Boolean(details && details.hasAttribute("open"));
  }

  function handleTrackingClick(event) {
    const element = event.target.closest(TRACKING_SELECTOR);
    if (!element) return;

    const action = getDatasetValue(element, "clarityMenuAction");
    const detail = getTrackingDetail(element);

    if (action === "open") {
      if (isClosingOpenDetails(element)) {
        trackMenuEvent("close", Object.assign({}, currentMenuContext, {
          surface: detail.surface || currentMenuContext.surface,
          closeSource: detail.closeSource || "toggle"
        }));
        resetLocalOpenCycle();
        return;
      }

      trackMenuOpen(element, detail);
      return;
    }

    if (action === "select") {
      trackMenuEvent("select", detail);
      return;
    }

    if (action === "back") {
      trackMenuEvent("back", detail);
      return;
    }

    if (action === "close") {
      trackMenuEvent("close", Object.assign({}, currentMenuContext, {
        surface: detail.surface || currentMenuContext.surface,
        closeSource: detail.closeSource || "icon"
      }));
      resetLocalOpenCycle();
    }
  }

  function handleHoverOpen(event) {
    const element = event.currentTarget;
    const detail = getTrackingDetail(element);
    const pathKey = detail.path || "root";

    if (openedPathsThisCycle[pathKey]) return;

    openedPathsThisCycle[pathKey] = true;
    setCurrentMenuContext(detail, getScrollContainerForElement(element));
    trackMenuEvent("open", detail);
  }

  function scheduleHoverOpen(event) {
    const element = event.currentTarget;

    clearTimeout(hoverOpenTimer);
    hoverOpenTimer = window.setTimeout(function () {
      handleHoverOpen({ currentTarget: element });
    }, HOVER_OPEN_DELAY);
  }

  function cancelHoverOpen() {
    clearTimeout(hoverOpenTimer);
    hoverOpenTimer = null;
  }

  function bindScrollTracking() {
    document.querySelectorAll(SCROLL_CONTAINER_SELECTOR).forEach(function (container) {
      container.addEventListener("scroll", trackMenuScrolled, { passive: true });
    });
  }

  function bindHoverTracking() {
    document.querySelectorAll(HOVER_SELECTOR).forEach(function (element) {
      element.addEventListener("mouseenter", scheduleHoverOpen);
      element.addEventListener("mouseleave", cancelHoverOpen);
      element.addEventListener("focusin", handleHoverOpen);
    });
  }

  function init() {
    document.addEventListener("click", handleTrackingClick, true);
    bindHoverTracking();
    bindScrollTracking();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
